import type React from 'react';
import {useCallback, useMemo} from 'react';
import {assert} from 'assert-ts';
import type {AgentSubType, CollectionSubType} from 'types';
import {Concept} from 'types';
import type {NationalAgentType} from 'services/agent/types';
import type {SavePrizeFunc} from 'services/prizes/types';
import type {SaveCollectionFunc} from 'services/series/types';
import type {SaveAgentFunc} from 'services/agent';
import {useAgent} from 'services/agent';
import {
  metadataEditAgentVerifiedChanged,
  useWorkForManifestation,
  useWorkMetadata,
} from 'services/metadata';
import {usePrize} from 'services/prizes';
import {useCollection} from 'services/series';
import {
  assertCollectionValue,
  assertPrizeValue,
  isAgentSubType,
} from 'services/utils';
import {assertAgentValue} from 'services/utils/functions/assertAgentValue';
import {isCollectionSubType} from 'services/utils/functions/isCollectionSubType';
import {useAppDispatch} from 'store/hooks';
// eslint-disable-next-line import-x/no-cycle
import {useEditAgentDialog} from 'editors/agent/hooks/useEditAgentDialog';
import {useEditAgentWizardDialog} from 'editors/agent/hooks/useEditAgentWizardDialog';
import {useEditCollectionDialog} from 'editors/collections/hooks/useEditCollectionDialog';
import {useEditPrizeDialog} from 'editors/prize';
import type {EntityState, EntityValue, SearchResultType} from '../types';

type EntityDialogConfig = {
  entityState?: EntityState;
  handleAdd?: (type: SearchResultType, suggestion: string) => void;
  handleEdit?: (value: EntityValue) => void;
  EntityDialog?: React.FC;
};

const NotImplementedEntityDialogConfig: EntityDialogConfig = {
  EntityDialog: () => {
    assert(false, 'useEntityDialogs: EntityDialog not implemented');
    return null;
  },
};

export const useEntityDialogs = (
  entityType: SearchResultType,
  entityId: string | undefined,
  onSaveEntity?: (entityValue: EntityValue) => void,
): EntityDialogConfig => {
  const agentId = isAgentSubType(entityType) ? entityId : undefined;
  const collectionId = isCollectionSubType(entityType) ? entityId : undefined;
  const prizeId = entityType === Concept.prize ? entityId : undefined;
  const workId = entityType === Concept.work ? entityId : undefined;
  const manifestationId =
    entityType === Concept.manifestation ? entityId : undefined;
  const {agent, saveAgent, deleteAgent} = useAgent(agentId, 'partial');
  const {collection, saveCollection} = useCollection(collectionId);
  const {prize, savePrize} = usePrize(prizeId);
  const work = useWorkMetadata(workId);
  const workForManifestation = useWorkForManifestation(manifestationId);

  const dispatch = useAppDispatch();

  const handleSaveAgent = useCallback<SaveAgentFunc>(
    (agent, options) => {
      return saveAgent(agent, options).then(savedAgent => {
        onSaveEntity && onSaveEntity(savedAgent);
        if (savedAgent.id && savedAgent.verified !== undefined) {
          dispatch(
            metadataEditAgentVerifiedChanged({
              agentId: savedAgent.id,
              verified: savedAgent.verified,
            }),
          );
        }
        // setViewEntity(savedAgent);
        return savedAgent;
      });
    },
    [dispatch, onSaveEntity, saveAgent],
  );
  const handleSaveCollection = useCallback<SaveCollectionFunc>(
    collection => {
      return saveCollection(collection).then(savedCollection => {
        onSaveEntity && onSaveEntity(savedCollection);
        return savedCollection;
      });
    },
    [onSaveEntity, saveCollection],
  );

  const handleSavePrize = useCallback<SavePrizeFunc>(
    prize => {
      return savePrize(prize).then(savedPrize => {
        onSaveEntity && onSaveEntity(savedPrize);
        return savedPrize;
      });
    },
    [onSaveEntity, savePrize],
  );

  const {handleAddAgent, handleEditAgent, EditAgentDialog} =
    useEditAgentDialog(handleSaveAgent);
  const {handleAddAgentWizard, handleEditAgentWizard, EditAgentWizard} =
    useEditAgentWizardDialog(handleSaveAgent, deleteAgent);
  const {handleAddCollection, handleEditCollection, EditCollectionDialog} =
    useEditCollectionDialog(handleSaveCollection);
  const {handleAddPrize, handleEditPrize, EditPrizeDialog} =
    useEditPrizeDialog(handleSavePrize);

  return useMemo(() => {
    const entityMap: {
      [key in SearchResultType]?: EntityDialogConfig;
    } = {
      person: {
        entityState: agent,
        handleAdd: (type: SearchResultType, suggestion: string) =>
          handleAddAgentWizard(type as NationalAgentType, suggestion),
        handleEdit: (value: EntityValue) =>
          handleEditAgentWizard(assertAgentValue(value)),
        EntityDialog: EditAgentWizard,
      },
      corporation: {
        entityState: agent,
        handleAdd: (type: SearchResultType, suggestion: string) =>
          handleAddAgentWizard(type as NationalAgentType, suggestion),
        handleEdit: (value: EntityValue) =>
          handleEditAgentWizard(assertAgentValue(value)),
        EntityDialog: EditAgentWizard,
      },
      event: {
        entityState: agent,
        handleAdd: (type: SearchResultType, suggestion: string) =>
          handleAddAgentWizard(type as NationalAgentType, suggestion),
        handleEdit: (value: EntityValue) =>
          handleEditAgentWizard(assertAgentValue(value)),
        EntityDialog: EditAgentWizard,
      },
      publisher: {
        entityState: agent,
        handleAdd: (type: SearchResultType, suggestion: string) =>
          handleAddAgent(type as AgentSubType, suggestion),
        handleEdit: (value: EntityValue) =>
          handleEditAgent(assertAgentValue(value)),
        EntityDialog: EditAgentDialog,
      },
      series: {
        entityState: collection,
        handleAdd: (type: SearchResultType) =>
          handleAddCollection(type as CollectionSubType),
        handleEdit: (value: EntityValue) =>
          handleEditCollection(assertCollectionValue(value)),
        EntityDialog: EditCollectionDialog,
      },
      educationalSeries: {
        entityState: collection,
        handleAdd: (type: SearchResultType) =>
          handleAddCollection(type as CollectionSubType),
        handleEdit: (value: EntityValue) =>
          handleEditCollection(assertCollectionValue(value)),
        EntityDialog: EditCollectionDialog,
      },
      otherConnection: {
        entityState: collection,
        handleAdd: (type: SearchResultType) =>
          handleAddCollection(type as CollectionSubType),
        handleEdit: (value: EntityValue) =>
          handleEditCollection(assertCollectionValue(value)),
        EntityDialog: EditCollectionDialog,
      },
      publisherSeries: {
        entityState: collection,
        handleAdd: (type: SearchResultType) =>
          handleAddCollection(type as CollectionSubType),
        handleEdit: (value: EntityValue) =>
          handleEditCollection(assertCollectionValue(value)),
        EntityDialog: EditCollectionDialog,
      },
      work: {
        entityState: work,
      },
      manifestation: {
        entityState: workForManifestation,
      },
      prize: {
        entityState: prize,
        handleAdd: handleAddPrize,
        handleEdit: (value: EntityValue) =>
          handleEditPrize(assertPrizeValue(value)),
        EntityDialog: EditPrizeDialog,
      },
    };

    return entityMap[entityType] ?? NotImplementedEntityDialogConfig;
  }, [
    agent,
    EditAgentWizard,
    EditAgentDialog,
    collection,
    EditCollectionDialog,
    work,
    workForManifestation,
    prize,
    handleAddPrize,
    EditPrizeDialog,
    entityType,
    handleAddAgentWizard,
    handleEditAgentWizard,
    handleAddAgent,
    handleEditAgent,
    handleAddCollection,
    handleEditCollection,
    handleEditPrize,
  ]);
};
